import { useEffect, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import { buildHannaGeorgeSchedule, isHannaGeorge } from "../tenants/gi/patientFacing/Instructions/hannaGeorgeSchedule";

const hannaTiming = {
  HannaGolytelyDiet: { anchor: "daysBefore", daysBefore: 2, time: "" },
  HannaGolytelyLiquids: { anchor: "daysBefore", daysBefore: 1, time: "" },
  HannaGolytelyFirst: { anchor: "daysBefore", daysBefore: 1, time: "18:00" },
  HannaGolytelySecond: { anchor: "hoursBefore", hoursBefore: 6 },
  HannaSuflaveDiet: { anchor: "daysBefore", daysBefore: 2, time: "" },
  HannaSuflaveLiquids: { anchor: "daysBefore", daysBefore: 1, time: "" },
  HannaSuflaveFirst: { anchor: "daysBefore", daysBefore: 1, time: "18:00" },
  HannaSuflaveSecond: { anchor: "hoursBefore", hoursBefore: 6 },
  HannaSuprepDiet: { anchor: "daysBefore", daysBefore: 2, time: "" },
  HannaSuprepLiquids: { anchor: "daysBefore", daysBefore: 1, time: "" },
  HannaSuprepFirst: { anchor: "daysBefore", daysBefore: 1, time: "18:00" },
  HannaSuprepSecond: { anchor: "hoursBefore", hoursBefore: 6 },
  HannaMiralaxDiet: { anchor: "daysBefore", daysBefore: 2, time: "" },
  HannaMiralaxLiquids: { anchor: "daysBefore", daysBefore: 1, time: "" },
  HannaMiralaxFirst: { anchor: "daysBefore", daysBefore: 1, time: "18:00" },
  HannaMiralaxSecond: { anchor: "daysBefore", daysBefore: 1, time: "21:00" },
};

const normalize = (name) => (name || "").trim().toLowerCase();

export default function DoctorProtocolEditor({ doctor, onUpdated }) {
  const preps = [...new Set((doctor.medicationGroups || []).flatMap((group) => group.options || []))];
  const [prepName, setPrepName] = useState(preps[0] || "");
  const [steps, setSteps] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const protocols = doctor.instructionProtocols || {};
  const draft = (protocols.drafts || []).find((entry) => normalize(entry.prepName) === normalize(prepName));
  const published = (protocols.published || []).find((entry) => normalize(entry.prepName) === normalize(prepName));

  useEffect(() => {
    setSteps((draft || published)?.steps?.map((step) => ({ ...step })) || []);
    setDirty(false);
    setError("");
    setMessage("");
  }, [doctor.id, prepName, doctor.instructionProtocols]);

  const updateStep = (index, field, value) => {
    setSteps((current) => current.map((step, i) => {
      if (i !== index) return step;
      if (field === "anchor" && value === "hoursBefore") {
        return { ...step, anchor: value, hoursBefore: step.hoursBefore ?? 6 };
      }
      if (field === "anchor" && value === "daysBefore") {
        return { ...step, anchor: value, daysBefore: step.daysBefore ?? 1, time: step.time ?? "" };
      }
      return { ...step, [field]: value };
    }));
    setDirty(true);
  };

  const addStep = () => {
    setSteps((current) => [...current, { anchor: "daysBefore", daysBefore: 1, time: "", text: "" }]);
    setDirty(true);
  };

  const moveStep = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= steps.length) return;
    setSteps((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  };

  const loadHanna = () => {
    if (steps.length && !window.confirm("Replace the unsaved steps with the current Hanna schedule?")) return;
    const source = buildHannaGeorgeSchedule(new Date(2026, 0, 10), "08:00", prepName);
    if (!source) return setError("No built-in Hanna schedule exists for this prep.");
    setSteps(source.map(({ content, description }) => ({ ...hannaTiming[content], text: description })));
    setDirty(true);
    setError("");
  };

  const save = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const { data } = await axios.put(`/api/admin/doctors/${doctor.id}/protocol-drafts`, { prepName, steps });
      onUpdated(data.data);
      setDirty(false);
      setMessage("Draft saved. Patients will continue to use the last published version.");
    } catch (err) {
      setError(err.response?.data?.error || "Could not save protocol draft.");
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!window.confirm("Has a clinician reviewed this prep text and timing? Publishing affects NEW cases only.")) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const { data } = await axios.post(`/api/admin/doctors/${doctor.id}/protocol-publish`, {
        prepName, reviewed: true,
      });
      onUpdated(data.data);
      setMessage("Published for new cases. Existing cases retain their saved protocol.");
    } catch (err) {
      setError(err.response?.data?.error || "Could not publish protocol.");
    } finally {
      setBusy(false);
    }
  };

  if (!doctor.id) return <Hint>Save the doctor before creating a protocol.</Hint>;
  return (
    <Container>
      <h3>GI preparation protocols</h3>
      <Hint>
        Set separate instructions for each selected prep. Save a draft, obtain clinical approval,
        then publish. Published instructions are copied into new cases; edits do not change existing cases.
        Save changes to Laxative Groups and the doctor before editing protocols.
        Text is English-only until approved translations are available.
      </Hint>
      <label htmlFor="protocol-prep">Prep</label>
      <Select id="protocol-prep" value={prepName} onChange={(e) => {
        if (dirty && !window.confirm("Discard unsaved changes to this prep?")) return;
        setPrepName(e.target.value);
      }}>
        {preps.length === 0 && <option value="">Select a prep in Laxative Groups first</option>}
        {preps.map((prep) => <option key={prep} value={prep}>{prep}</option>)}
      </Select>
      {prepName && (
        <>
          <Hint>{published ? `Published version ${published.version}` : "Not published"}; {draft ? "draft saved" : "no saved draft"}.</Hint>
          {isHannaGeorge(doctor.name) && (
            <Button type="button" onClick={loadHanna}>Load current Hanna instructions into draft</Button>
          )}
          {steps.map((step, index) => (
            <Step key={index}>
              <label>
                Timing
                <Select value={step.anchor} onChange={(e) => updateStep(index, "anchor", e.target.value)}>
                  <option value="daysBefore">Days before appointment</option>
                  <option value="hoursBefore">Hours before appointment</option>
                </Select>
              </label>
              {step.anchor === "hoursBefore" ? (
                <label>Hours before (1-72)
                  <Input type="number" min="1" max="72" value={step.hoursBefore ?? 6}
                    onChange={(e) => updateStep(index, "hoursBefore", e.target.value)} />
                </label>
              ) : (
                <Row>
                  <label>Days before (0-14)
                    <Input type="number" min="0" max="14" value={step.daysBefore ?? 1}
                      onChange={(e) => updateStep(index, "daysBefore", e.target.value)} />
                  </label>
                  <label>Time (blank = date only)
                    <Input type="time" value={step.time || ""}
                      onChange={(e) => updateStep(index, "time", e.target.value)} />
                  </label>
                </Row>
              )}
              <label>Patient instructions (English)
                <Textarea maxLength={1200} value={step.text || ""}
                  onChange={(e) => updateStep(index, "text", e.target.value)} />
              </label>
              <Row>
                <Button type="button" disabled={index === 0} onClick={() => moveStep(index, -1)}>Up</Button>
                <Button type="button" disabled={index === steps.length - 1} onClick={() => moveStep(index, 1)}>Down</Button>
                <Button type="button" onClick={() => {
                  setSteps((current) => current.filter((_, i) => i !== index));
                  setDirty(true);
                }}>Remove</Button>
              </Row>
            </Step>
          ))}
          <Button type="button" onClick={addStep}>Add instruction step</Button>
          {error && <Warning role="alert">{error}</Warning>}
          {message && <Hint role="status">{message}</Hint>}
          <Row>
            <Button type="button" disabled={busy || steps.length === 0} onClick={save}>Save draft</Button>
            <Publish type="button" disabled={busy || dirty || !draft} onClick={publish}>Publish reviewed draft</Publish>
          </Row>
        </>
      )}
    </Container>
  );
}

const Container = styled.section`
  border-top: 1px solid #ddd;
  padding-top: 1rem;
  h3 { margin: 0 0 0.5rem; }
  label { display: flex; flex-direction: column; gap: 0.35rem; }
`;
const Hint = styled.p`font-size: 0.9rem; color: #555;`;
const Warning = styled(Hint)`color: #803d00; background: #fff5e7; padding: 0.7rem;`;
const Row = styled.div`display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: flex-end;`;
const Step = styled.div`
  display: flex; flex-direction: column; gap: 0.7rem;
  margin: 0.8rem 0; padding: 0.8rem;
  border: 1px solid #ddd; border-radius: 6px;
`;
const Input = styled.input`padding: 0.5rem; border: 1px solid #bbb; border-radius: 4px;`;
const Select = styled.select`padding: 0.5rem; border: 1px solid #bbb; border-radius: 4px;`;
const Textarea = styled.textarea`min-height: 70px; padding: 0.5rem; border: 1px solid #bbb; border-radius: 4px;`;
const Button = styled.button`
  padding: 0.5rem; margin: 0.25rem 0;
  border: 1px solid #bbb; border-radius: 4px;
  background: #fff; cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const Publish = styled(Button)`background: #214c72; color: white; border-color: #214c72;`;
